import {mediaQuality, mediaTypes} from "@/domain/galleries/constants"
import metric_message from "@/domain/metrics/message"

const NATIVE_SHARE_PLATFORM = /iPhone|iPad/i
const DEFAULT_IDLE_AFTER_MS = 800

export function resolveMediaDownloadUrl(media, quality = mediaQuality.AUTO) {
    if (!media) return null

    if (quality === mediaQuality.AUTO) {
        return media.download_hd_url || media.download_sd_url || null
    }

    if (quality === mediaQuality.SD) {
        if (media.media_type === mediaTypes.VIDEO) {
            return media.download_hd_url || media.video_preview_url || media.download_sd_url || null
        }
        return media.download_sd_url || null
    }

    if (quality === mediaQuality.HD) {
        if (!media.download_hd_url) return null
        return media.download_hd_url.includes('cdn.getpica.com')
            ? `${media.download_hd_url}/download`
            : media.download_hd_url
    }

    return null
}

export function canNativeShare() {
    return typeof navigator.share === 'function'
        && NATIVE_SHARE_PLATFORM.test(navigator.userAgent)
}

export function trackMediaDownload(media) {
    metric_message.photoDownloaded(media.id, media.media_type === mediaTypes.VIDEO)
}

/**
 * Triggers a single media download. On iPhone/iPad with `navigator.share`
 * available, opens the OS share sheet with the file; otherwise falls back
 * to a same-window navigation to the media URL (CDN serves it as an
 * attachment).
 *
 * @param {Object} media
 * @param {Object} [options]
 * @param {string} [options.quality=mediaQuality.AUTO]
 * @param {boolean} [options.trackMetric=true]
 * @param {number} [options.idleAfterMs=1000]   Delay after triggering the
 *   non-native download. Useful in loops, so the browser has time to start
 *   each download before the next href change.
 * @returns {Promise<boolean>} true when a download was initiated.
 */
export async function downloadMedia(media, options = {}) {
    const {
        quality = mediaQuality.AUTO,
        trackMetric = true,
        idleAfterMs = DEFAULT_IDLE_AFTER_MS,
    } = options

    const url = resolveMediaDownloadUrl(media, quality)
    if (!url) return false

    if (trackMetric) {
        trackMediaDownload(media)
    }

    if (canNativeShare()) {
        const file = await fetchMediaFile(media, url)
        if (file && await shareFiles([file])) return true
        triggerBrowserDownload(url)
        return true
    }

    triggerBrowserDownload(url)
    if (idleAfterMs > 0) {
        await wait(idleAfterMs)
    }
    return true
}

async function fetchMediaFile(media, url, signal) {
    try {
        const response = await fetch(url, {method: 'GET', mode: 'cors', signal})
        const blob = await response.blob()
        const isVideo = media.media_type === mediaTypes.VIDEO
        const extension = isVideo ? 'mp4' : 'jpg'
        const mimeType = isVideo ? 'video/mp4' : 'image/jpeg'
        return new File([blob], `media_${media.id}.${extension}`, {type: mimeType})
    } catch (error) {
        return null
    }
}

async function shareFiles(files) {
    if (!(navigator.canShare && navigator.canShare({files}))) return false
    try {
        await navigator.share({files})
        return true
    } catch (error) {
        return error.name === 'AbortError'
    }
}

function triggerBrowserDownload(url) {
    location.href = url
}

function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}
